/**
 * Deterministic RNG for noise layers — part of the cross-engine contract.
 *
 * Per-layer seed = fnv1a32(layer.id), expanded to xoshiro128++ state via
 * splitmix32. Port this file operation-for-operation to Kotlin (all 32-bit
 * unsigned arithmetic) so noise output can be compared sample-exactly across
 * engines.
 */

/** FNV-1a 32-bit hash of a string (UTF-16 code units). */
export function fnv1a32(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0; // FNV prime, 32-bit wrapping multiply
  }
  return h >>> 0;
}

/** splitmix32 — used only to expand a 32-bit seed into xoshiro state. */
function splitmix32(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x9e3779b9) >>> 0;
    let z = s;
    z = Math.imul(z ^ (z >>> 16), 0x21f0aaad) >>> 0;
    z = Math.imul(z ^ (z >>> 15), 0x735a2d97) >>> 0;
    return (z ^ (z >>> 15)) >>> 0;
  };
}

function rotl(x: number, k: number): number {
  return ((x << k) | (x >>> (32 - k))) >>> 0;
}

/** xoshiro128++ — small, fast, portable, good statistical quality. */
export class Xoshiro128pp {
  private s0: number;
  private s1: number;
  private s2: number;
  private s3: number;

  constructor(seed: number) {
    const sm = splitmix32(seed);
    this.s0 = sm();
    this.s1 = sm();
    this.s2 = sm();
    this.s3 = sm();
    // All-zero state is invalid; splitmix32 makes it astronomically unlikely,
    // but guard anyway so the contract has no undefined corner.
    if ((this.s0 | this.s1 | this.s2 | this.s3) === 0) this.s3 = 1;
  }

  nextUint32(): number {
    const result = (rotl((this.s0 + this.s3) >>> 0, 7) + this.s0) >>> 0;
    const t = (this.s1 << 9) >>> 0;
    this.s2 = (this.s2 ^ this.s0) >>> 0;
    this.s3 = (this.s3 ^ this.s1) >>> 0;
    this.s1 = (this.s1 ^ this.s2) >>> 0;
    this.s0 = (this.s0 ^ this.s3) >>> 0;
    this.s2 = (this.s2 ^ t) >>> 0;
    this.s3 = rotl(this.s3, 11);
    return result;
  }

  /** Uniform float in [-1, 1). */
  nextFloat(): number {
    return this.nextUint32() * (2 / 4294967296) - 1;
  }
}
